/**
 * B2Ctelecom — product-mobile-media.js (fase 1 + fase 2, 2026-09)
 *
 * Fase 1: Shopify-gehoste product.media video's (media_type == 'video')
 * volledig uitsluiten uit de productgallery op mobiel (Dawn-breakpoint:
 * <= 749px), terwijl desktop (>= 750px) exact ongewijzigd blijft.
 *
 * Fase 2: de mobiele video uit het productmetafield
 * custom.mobile_product_video wordt door de Liquid-snippets gerenderd met
 * data-media-visibility="mobile-only" (laatste slide + thumbnail; bewust
 * NIET in de modal) en door DIT ZELFDE mechanisme gespiegeld beheerd:
 * aanwezig op mobiel, via Flickity remove/insert volledig verwijderd op
 * desktop. De video is een INLINE Shopify <video> (native controls,
 * playsinline, geen autoplay/loop); dit script regelt daarnaast:
 * - uitklappen: standaard is de video-cell even hoog als de
 *   afbeeldingsslides (geen CLS, afbeeldingen ongewijzigd); alleen wanneer
 *   de cell geselecteerd is krijgt hij min(cap, breedte/ratio) hoogte
 *   (cap: 85vh pilotwaarde voor de instructie-screenvideo met class
 *   b2c-mobile-instruction-video, edge-to-edge met object-fit cover via
 *   de CSS in product-media-gallery.liquid; 70vh voor overige mobiele
 *   video's), gevolgd door flickity.resize(), en wordt de wrapper-
 *   hoogtebeperking tijdelijk opgeheven (class b2c-mobile-video-active);
 *   de class b2c-mobile-video-playing volgt de echte play/pause/ended-
 *   events en is puur informatief (geen layout-trigger);
 * - autoplay-guard: de select-handler in global.js roept play() aan op de
 *   video van de geselecteerde cell; een play zonder recente aanraking van
 *   de video zelf wordt direct weer gepauzeerd, zodat swipen naar de slide
 *   de video nooit vanzelf start (de native controls werken gewoon).
 * Er is bewust géén tweede los script.
 *
 * Werking (bewust NIET alleen CSS):
 * - Slides met data-media-visibility="desktop-only" worden op mobiel via de
 *   Flickity-API (flickity.remove) uit de carousel gehaald en bij terugkeer
 *   naar desktop op hun oorspronkelijke, bij paginalaad vastgelegde index
 *   teruggeplaatst (flickity.insert). Flickity's cell-model, swipe-posities
 *   en selectie blijven daardoor correct: geen lege swipe-posities, geen
 *   ghost-cells, geen video-audio via de select-handler in global.js.
 * - Video-thumbnails worden op dezelfde manier verwijderd/teruggeplaatst en
 *   de door FlickityThumbnails (global.js) gecachte NodeList
 *   (element.carouselThumbnails) wordt ververst zodat de aria-current-
 *   markering op de juiste thumbnail blijft staan.
 * - Modal/lightbox-items met data-media-visibility="desktop-only" worden op
 *   mobiel eveneens fysiek uit de DOM verwijderd (dus ook niet bereikbaar
 *   via keyboard/focus of verborgen DOM-posities). Een MutationObserver
 *   vangt de innerHTML-hersync van de modal af die
 *   VariantSelects.updateMedia (global.js) bij variantwissel uitvoert; de
 *   CSS in product-media-gallery.liquid blijft als extra vangnet staan.
 * - Er wordt uitsluitend op matchMedia('(min-width: 750px)') 'change'
 *   gereageerd; geen window-resize-polling. Bij herhaald resizen over het
 *   breakpoint worden altijd dezelfde node-referenties hergebruikt: geen
 *   dubbele cells, thumbnails, IDs of event listeners.
 * - Elk onderdeel is in try/catch geisoleerd: als dit script faalt, blijft
 *   de bestaande gallery (afbeeldingen, swipe, varianten) gewoon werken.
 *
 * De select-handler in global.js pauzeert video's automatisch bij elke
 * slide-wissel; dit script pauzeert daarnaast elke video vóór verwijdering,
 * zodat er nooit achtergrondaudio kan blijven spelen.
 */
(function () {
  'use strict';

  if (window.B2CProductMobileMedia) return;
  window.B2CProductMobileMedia = true;

  // Dawn-conventie: mobiel <= 749px / desktop >= 750px (zie .small-hide in base.css)
  var desktopMql = window.matchMedia('(min-width: 750px)');

  var VISIBILITIES = ['desktop-only', 'mobile-only'];
  var galleries = [];
  var modals = [];
  var modalObservers = [];

  function safe(fn) {
    try {
      fn();
    } catch (error) {
      if (window.console && console.warn) {
        console.warn('[product-mobile-media] genegeerde fout:', error);
      }
    }
  }

  function getFlickity(carouselEl) {
    if (!carouselEl) return null;
    if (carouselEl.flickity) return carouselEl.flickity;
    if (window.Flickity && typeof window.Flickity.data === 'function') {
      return window.Flickity.data(carouselEl) || null;
    }
    return null;
  }

  function pauseVideosIn(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('video').forEach(function (video) {
      try {
        video.pause();
      } catch (e) {
        /* noop */
      }
    });
  }

  function emptyGroups() {
    var groups = {};
    VISIBILITIES.forEach(function (key) {
      groups[key] = [];
    });
    return groups;
  }

  /* ------------------------------------------------------------------ *
   * Gallery (Flickity-slider + thumbnail-strip)
   * ------------------------------------------------------------------ */

  function collectGalleries() {
    galleries = [];

    document.querySelectorAll('media-gallery').forEach(function (galleryEl) {
      var carousel = galleryEl.querySelector('flickity-carousel');
      if (!carousel) return;

      var flickity = getFlickity(carousel);
      var allSlides = flickity && flickity.cells && flickity.cells.length
        ? flickity.cells.map(function (cell) { return cell.element; })
        : Array.prototype.slice.call(carousel.querySelectorAll('.product__media-item'));

      var gallery = {
        carousel: carousel,
        thumbs: galleryEl.querySelector('flickity-thumbnails'),
        slides: emptyGroups(),
        thumbItems: emptyGroups()
      };

      // Oorspronkelijke DOM-index bij paginalaad vastleggen (voorwaarde 3):
      // dit is de terugplaats-positie bij resize mobiel -> desktop.
      allSlides.forEach(function (slide, index) {
        var visibility = slide.getAttribute('data-media-visibility');
        if (gallery.slides[visibility]) {
          gallery.slides[visibility].push({ el: slide, index: index });
        }
      });

      if (gallery.thumbs) {
        var allThumbs = gallery.thumbs.querySelectorAll('.thumbnail-list__item');
        Array.prototype.forEach.call(allThumbs, function (thumb, index) {
          var visibility = thumb.getAttribute('data-media-visibility');
          if (gallery.thumbItems[visibility]) {
            gallery.thumbItems[visibility].push({ el: thumb, index: index });
          }
        });
      }

      var hasItems = VISIBILITIES.some(function (key) {
        return gallery.slides[key].length || gallery.thumbItems[key].length;
      });
      if (hasItems) galleries.push(gallery);
    });
  }

  function removeSlides(gallery, visibility) {
    var items = gallery.slides[visibility].filter(function (item) {
      return item.el.isConnected;
    });
    if (!items.length) return;

    items.forEach(function (item) {
      pauseVideosIn(item.el);
    });

    var flickity = getFlickity(gallery.carousel);
    if (flickity && flickity.isActive) {
      // Flickity haalt de cellen uit zijn cell-model EN uit de DOM;
      // aantallen, posities en selectie worden intern bijgewerkt.
      flickity.remove(items.map(function (item) { return item.el; }));
      flickity.resize();
    } else {
      items.forEach(function (item) {
        item.el.remove();
      });
    }
  }

  function restoreSlides(gallery, visibility) {
    var items = gallery.slides[visibility]
      .filter(function (item) { return !item.el.isConnected; })
      .sort(function (a, b) { return a.index - b.index; });
    if (!items.length) return;

    var flickity = getFlickity(gallery.carousel);
    if (flickity && flickity.isActive) {
      items.forEach(function (item) {
        // Oplopend op originele index invoegen zet elk item exact terug
        // op zijn oorspronkelijke positie.
        flickity.insert(item.el, Math.min(item.index, flickity.cells.length));
      });
      flickity.resize();
    } else {
      items.forEach(function (item) {
        var current = gallery.carousel.querySelectorAll('.product__media-item');
        var ref = current[item.index] || null;
        var parent = ref
          ? ref.parentNode
          : gallery.carousel.querySelector('.flickity-slider') || gallery.carousel;
        parent.insertBefore(item.el, ref);
      });
    }
  }

  function removeThumbs(gallery, visibility) {
    gallery.thumbItems[visibility].forEach(function (item) {
      if (item.el.isConnected) item.el.remove();
    });
  }

  function restoreThumbs(gallery, visibility) {
    if (!gallery.thumbs) return;
    gallery.thumbItems[visibility]
      .filter(function (item) { return !item.el.isConnected; })
      .sort(function (a, b) { return a.index - b.index; })
      .forEach(function (item) {
        var current = gallery.thumbs.querySelectorAll('.thumbnail-list__item');
        var ref = current[item.index] || gallery.thumbs.querySelector('.slider-button--next');
        gallery.thumbs.insertBefore(item.el, ref || null);
      });
  }

  function syncThumbnails(gallery) {
    if (!gallery.thumbs) return;

    // FlickityThumbnails (global.js) cachet de thumbnail-NodeList in de
    // constructor en gebruikt die positioneel voor de aria-current-markering.
    // Na het toevoegen/verwijderen van items moet die cache mee veranderen,
    // anders wijst selectedIndex naar de verkeerde thumbnail. De bestaande
    // click-listeners blijven op de (hergebruikte) nodes zelf staan; er wordt
    // dus niets opnieuw gebonden.
    gallery.thumbs.carouselThumbnails = gallery.thumbs.querySelectorAll('.slider__slide');

    var flickity = getFlickity(gallery.carousel);
    if (!flickity || !flickity.isActive || !flickity.cells.length) return;

    var selectedCell = flickity.cells[flickity.selectedIndex];
    var mediaId = selectedCell && selectedCell.element.getAttribute('data-media-id');
    if (!mediaId) return;

    var target = gallery.thumbs.querySelector(
      '.thumbnail-list__item[data-target="' + mediaId + '"] .thumbnail'
    );
    if (!target) return;

    gallery.thumbs.querySelectorAll('.thumbnail[aria-current]').forEach(function (el) {
      if (el !== target) el.removeAttribute('aria-current');
    });
    target.setAttribute('aria-current', 'true');
  }

  /* ------------------------------------------------------------------ *
   * Fase 2: inline mobiele video — uitklappen + autoplay-guard
   * ------------------------------------------------------------------ */

  function requestFlickityResize(flickity) {
    // Flickity's resize() is een no-op tijdens isAnimating/isDragging
    // (vendor.js v2.3.0). Bij een select via swipe of thumbnail-klik loopt
    // de settle-animatie nog; plan de resize dan op het 'settle'-event,
    // met een timeout als vangnet. resize() is idempotent (alleen
    // re-measure/reposition), dus dubbel aanroepen kan geen kwaad.
    if (!flickity || !flickity.isActive) return;
    if (!flickity.isAnimating && !flickity.isDragging) {
      flickity.resize();
      return;
    }
    var done = false;
    var run = function () {
      if (done) return;
      done = true;
      safe(function () {
        if (flickity.isActive) flickity.resize();
      });
    };
    if (typeof flickity.once === 'function') {
      flickity.once('settle', run);
    }
    setTimeout(run, 400);
  }

  function getMobileVideoParts(gallery) {
    // De instructievideo wordt EXPLICIET gevonden via zijn eigen class,
    // nooit via de positie in de mobile-only-groep: sinds de normale
    // mobiele productvideo (custom.mobile_normal_video) kan er meer dan één
    // mobile-only slide zijn, en die mag deze instructielogica (expand,
    // guard, custom controls, poster, reset) nooit bereiken.
    var item = null;
    gallery.slides['mobile-only'].some(function (candidate) {
      if (candidate.el.querySelector('.b2c-mobile-instruction-video')) {
        item = candidate;
        return true;
      }
      return false;
    });
    if (!item) return null;
    var media = item.el.querySelector('.b2c-mobile-instruction-video');
    var video = media && media.querySelector('video');
    if (!media || !video) return null;
    return { cell: item.el, media: media, video: video };
  }

  /* ------------------------------------------------------------------ *
   * Normale mobiele productvideo (custom.mobile_normal_video)
   * ------------------------------------------------------------------ */

  // Venster na een Flickity-select waarin een play() op de normale video
  // als automatisch (global.js) geldt en direct wordt teruggedraaid.
  var B2C_NORMAL_SELECT_BLOCK_MS = 500;

  function setupNormalVideoGuards(gallery) {
    // Apart autoplay-slot, volledig los van de instructielogica. De video
    // bestaat pas na een tik op de poster (<deferred-media> van de theme).
    // De select-handler in global.js roept bij swipen of thumbnail-klik
    // naar deze slide synchroon play() aan op de video van de geselecteerde
    // cell. Alleen DIE play wordt geblokkeerd: markNormalVideoSelect() zet
    // bij elke select van deze slide een kort blokkeervenster, en een
    // play-event binnen dat venster wordt direct gepauzeerd. Elke andere
    // play (poster-tik, native play-knop, ook na een pauze) is een bewuste
    // actie en blijft toegestaan. Bewust GEEN gebaar-detectie: tikken op de
    // native videobediening zijn voor de pagina niet zichtbaar, waardoor
    // een hervatting na pauze anders onterecht werd geblokkeerd.
    // Pauzeren bij slide verlaten doet global.js al.
    gallery.slides['mobile-only'].forEach(function (item) {
      var root = item.el.querySelector('.b2c-mobile-normal-video');
      if (!root || root.b2cNormalGuardBound) return;
      root.b2cNormalGuardBound = true;
      root.b2cBlockPlayUntil = 0;

      // 'play' bubbelt niet, maar is in de capture-fase op de container
      // wel te onderscheppen - ook voor de pas later ingevoegde <video>.
      // Het play-event wordt asynchroon afgevuurd, dus altijd NA de
      // select-handlers (ook als global.js zijn handler eerder bond).
      root.addEventListener('play', function (event) {
        var target = event.target;
        if (!target || target.nodeName !== 'VIDEO') return;
        if (Date.now() < root.b2cBlockPlayUntil) {
          try {
            target.pause();
          } catch (e) {
            /* noop */
          }
        }
      }, true);
    });
  }

  function markNormalVideoSelect(gallery) {
    var flickity = getFlickity(gallery.carousel);
    var selected = flickity && flickity.selectedElement;
    if (!selected) return;
    var root = selected.querySelector('.b2c-mobile-normal-video');
    if (root) root.b2cBlockPlayUntil = Date.now() + B2C_NORMAL_SELECT_BLOCK_MS;
  }

  function setupMobileVideoGuard(parts) {
    if (parts.video.b2cGuardBound) return;
    parts.video.b2cGuardBound = true;

    // Autoplay-guard: alleen een play die (vrijwel) direct volgt op een
    // aanraking van de video of zijn custom controls (tik, toets) is
    // toegestaan. De play() uit de select-handler van global.js — bij
    // swipen of thumbnail-klik naar deze slide — wordt zo direct weer
    // gepauzeerd. Gebonden op de media-container zodat ook de custom
    // play/seek/skip-bediening als gebruikersgebaar telt.
    var lastGesture = 0;
    ['pointerdown', 'touchstart', 'keydown'].forEach(function (type) {
      parts.media.addEventListener(type, function () {
        lastGesture = Date.now();
      }, true);
    });
    parts.video.addEventListener('play', function () {
      if (Date.now() - lastGesture > 1000) {
        try {
          parts.video.pause();
          // De geblokkeerde micro-play kan de positie enkele milliseconden
          // opschuiven; terug naar exact 0:00 zodat elk (her)bezoek vers
          // vanaf het begin start.
          parts.video.currentTime = 0;
          // Custom posterlaag weer tonen. De eerdere video.load()-hack is
          // bewust verwijderd: iOS Safari hertekent de native poster niet
          // na load() (zwart vlak); de <img>-posterlaag dekt het videovlak
          // betrouwbaar af op iOS en Android.
          parts.cell.classList.remove('b2c-poster-hidden');
        } catch (e) {
          /* noop */
        }
      } else {
        // Echte, door de gebruiker gestarte playback: posterlaag weg.
        parts.cell.classList.add('b2c-poster-hidden');
      }
    });
    // Bij afgelopen video komt de posterlaag terug en gaat de positie
    // terug naar 0:00, klaar voor een verse start (bij een gewone pauze
    // bewust niet: dan blijven videoframe en positie behouden).
    parts.video.addEventListener('ended', function () {
      parts.cell.classList.remove('b2c-poster-hidden');
      try {
        parts.video.currentTime = 0;
      } catch (e) {
        /* noop */
      }
    });

    // Informatieve playing-state (b2c-mobile-video-playing op de cell):
    // GEEN layout-trigger — de edge-to-edge maat hangt aan de
    // expanded/selected-staat. Deze class volgt puur de echte
    // play/pause/ended-events, o.a. voor eventuele latere styling.
    parts.video.addEventListener('play', function () {
      parts.cell.classList.add('b2c-mobile-video-playing');
    });
    // 'emptied' hoort erbij: het posterherstel via load() hierboven kan het
    // pause-event verzwelgen, maar vuurt altijd een emptied-event.
    ['pause', 'ended', 'emptied'].forEach(function (type) {
      parts.video.addEventListener(type, function () {
        parts.cell.classList.remove('b2c-mobile-video-playing');
      });
    });
  }

  function setupCustomControls(parts) {
    // Custom inline bediening — UITSLUITEND voor de instructievideo
    // (markup wordt alleen in het b2c-mobile-instruction-video-blok
    // gerenderd; andere video's houden hun native controls en bereiken
    // deze code nooit).
    var root = parts.media.querySelector('[data-b2c-video-controls]');
    if (!root || root.b2cControlsBound) return;
    root.b2cControlsBound = true;

    // Native controls gegarandeerd uit: Shopify's media_tag zet het
    // controls-attribuut standaard aan (ongeacht de filter-parameters, per
    // thema-versie wisselend). Hier verwijderen dekt elke variant af en
    // geeft nette degradatie: faalt dit script, dan blijven de native
    // controls staan en is de video alsnog bedienbaar.
    try {
      parts.video.removeAttribute('controls');
    } catch (e) {
      /* noop */
    }

    var video = parts.video;
    var cell = parts.cell;
    var toggleBtn = root.querySelector('[data-b2c-video-toggle]');
    // De seek/skip-bar staat als flow-element ONDER het videobeeld (sibling
    // van de overlay), dus zoeken vanaf de media-container.
    var bar = parts.media.querySelector('.b2c-video-bottom-bar');
    var seek = parts.media.querySelector('[data-b2c-video-seek]');
    var skips = parts.media.querySelectorAll('[data-b2c-video-skip]');
    var seeking = false;

    // Flickity-isolatie: input die start op de bedieningselementen mag
    // nooit een gallery-swipe worden. unidragger (vendor.js) ontvangt
    // pointer/touch-starts via bubbling op het carousel-element; hier
    // stoppen betekent: seek-drag blijft seek-drag. Zowel op de overlay
    // (centrale toggle) als op de losse bar gebonden.
    ['pointerdown', 'touchstart', 'mousedown'].forEach(function (type) {
      root.addEventListener(type, function (event) {
        if (event.target === root) return; // overlay zelf is pointer-events:none
        event.stopPropagation();
      });
      if (bar) {
        bar.addEventListener(type, function (event) {
          event.stopPropagation();
        });
      }
    });

    function togglePlayback() {
      try {
        if (video.paused || video.ended) {
          video.play();
        } else {
          video.pause();
        }
      } catch (e) {
        /* noop */
      }
    }

    if (toggleBtn) {
      toggleBtn.addEventListener('click', function (event) {
        event.stopPropagation();
        togglePlayback();
      });
    }

    // Tik op de video zelf = toggle (TikTok-patroon: tijdens afspelen is
    // de centrale knop verborgen en pauzeert een tik op het beeld).
    // Tijdens een Flickity-drag zet global.js pointer-events:none op de
    // slider, dus dit vuurt alleen bij echte taps — swipen blijft swipen.
    parts.media.addEventListener('click', function (event) {
      if (root.contains(event.target) && event.target !== root) return;
      if (bar && bar.contains(event.target)) return;
      togglePlayback();
    });

    function syncToggleState() {
      if (!toggleBtn) return;
      var playing = !video.paused && !video.ended;
      toggleBtn.setAttribute('aria-label', playing ? 'Video pauzeren' : 'Video afspelen');
    }
    ['play', 'pause', 'ended', 'emptied'].forEach(function (type) {
      video.addEventListener(type, syncToggleState);
    });

    // Seekbar: één bron van waarheid per richting — tijdens slepen wint
    // de gebruiker (geen timeupdate-overschrijving), daarbuiten de video.
    if (seek) {
      ['pointerdown', 'touchstart'].forEach(function (type) {
        seek.addEventListener(type, function () {
          seeking = true;
        });
      });
      ['pointerup', 'touchend', 'touchcancel', 'change'].forEach(function (type) {
        seek.addEventListener(type, function () {
          seeking = false;
        });
      });
      seek.addEventListener('input', function () {
        if (video.duration) {
          video.currentTime = (parseFloat(seek.value) / 100) * video.duration;
        }
      });
      video.addEventListener('timeupdate', function () {
        if (!seeking && video.duration) {
          seek.value = String((video.currentTime / video.duration) * 100);
        }
      });
      ['loadedmetadata', 'emptied'].forEach(function (type) {
        video.addEventListener(type, function () {
          if (!seeking) seek.value = '0';
        });
      });
    }

    Array.prototype.forEach.call(skips, function (btn) {
      btn.addEventListener('click', function (event) {
        event.stopPropagation();
        var delta = parseFloat(btn.getAttribute('data-b2c-video-skip')) || 0;
        try {
          var max = video.duration || Infinity;
          video.currentTime = Math.min(Math.max(video.currentTime + delta, 0), max);
        } catch (e) {
          /* noop */
        }
      });
    });

    // Buffering-state: spinner tonen zolang de video op data wacht.
    video.addEventListener('waiting', function () {
      cell.classList.add('b2c-video-buffering');
    });
    ['playing', 'canplay', 'pause', 'seeked', 'emptied', 'ended'].forEach(function (type) {
      video.addEventListener(type, function () {
        cell.classList.remove('b2c-video-buffering');
      });
    });

    syncToggleState();
  }

  function updateMobileVideoExpand(gallery) {
    var parts = getMobileVideoParts(gallery);
    if (!parts) return;

    setupMobileVideoGuard(parts);
    setupCustomControls(parts);

    var wrapper = gallery.carousel.closest('.product__media-wrapper');
    var flickity = getFlickity(gallery.carousel);
    var expanded = !desktopMql.matches &&
      parts.cell.isConnected &&
      flickity && flickity.isActive &&
      flickity.selectedElement === parts.cell;

    var changed = false;
    if (expanded) {
      if (!parts.cell.classList.contains('b2c-mobile-video-expanded')) {
        var ratio = parseFloat(parts.media.getAttribute('data-b2c-video-ratio')) || 0.5625;
        var width = parts.media.getBoundingClientRect().width ||
          parts.cell.getBoundingClientRect().width;
        // Hoogte-cap relatief aan de actuele viewport (geen device-detectie):
        // voor instructie-screenvideo's (b2c-mobile-instruction-video) is de
        // pilot-cap van 85vh het TOTAAL van videobeeld + controls-bar; de
        // barhoogte wordt live gemeten (offsetHeight, dus ook correct bij
        // grotere systeemfonts). Eerst de class zetten zodat de bar
        // zichtbaar en meetbaar is. Andere mobiele metafield-video's
        // (zonder bar) houden de behoudende 70vh uit de eerdere ronde.
        var capVh = parts.media.classList.contains('b2c-mobile-instruction-video') ? 0.85 : 0.7;
        parts.cell.classList.add('b2c-mobile-video-expanded');
        var bar = parts.media.querySelector('.b2c-video-bottom-bar');
        var barH = bar ? bar.offsetHeight : 0;
        var videoZone = Math.round(
          Math.min(window.innerHeight * capVh - barH, width / ratio)
        );
        if (videoZone > 0) parts.media.style.height = (videoZone + barH) + 'px';
        // Topcrop-clamp (alleen instructievideo, puur verhoudingen dus
        // device-onafhankelijk): object-position 50% 100% legt alle
        // verticale overflow bij de BOVENkant (onderste knoppen van de
        // opname blijven altijd volledig in beeld). Zou die topcrop meer
        // dan B2C_TOP_CROP_MAX van de bron worden, dan wordt het beeld
        // precies genoeg versmald (smalle zwarte zijstroken op de zwarte
        // spelerachtergrond) zodat de crop op dat maximum blijft.
        if (parts.media.classList.contains('b2c-mobile-instruction-video') && videoZone > 0) {
          var B2C_TOP_CROP_MAX = 0.10; // definitief na echte iPhone-check: max 10% boven (b2ctelecom.nl-balk van de opname volledig weg), 0% onder; X + template + onderste CTA geverifieerd zichtbaar
          var contentH = width / ratio;
          var maxContentH = videoZone / (1 - B2C_TOP_CROP_MAX);
          if (contentH > maxContentH) {
            parts.video.style.width = Math.round(maxContentH * ratio) + 'px';
          } else {
            parts.video.style.width = '';
          }
          // Posterlaag pixel-identiek aan het videobeeld houden (zelfde
          // breedte-clamp en zelfde hoogte als de videozone), zodat er bij
          // Play geen enkele visuele sprong is.
          var poster = parts.media.querySelector('.b2c-video-poster');
          if (poster) {
            poster.style.height = videoZone + 'px';
            poster.style.width = parts.video.style.width || (Math.round(width) + 'px');
          }
        }
        changed = true;
      }
    } else if (parts.cell.classList.contains('b2c-mobile-video-expanded')) {
      parts.media.style.height = '';
      parts.video.style.width = '';
      parts.cell.classList.remove('b2c-mobile-video-expanded');
      // Slide verlaten/collapse: pauzeren en terug naar 0:00 (het
      // herbezoek start dan altijd vers vanaf het begin, met de
      // posterlaag weer zichtbaar), geometrie terug naar de ruststand.
      try {
        parts.video.pause();
        parts.video.currentTime = 0;
      } catch (e) {
        /* noop */
      }
      parts.cell.classList.remove('b2c-poster-hidden');
      var posterReset = parts.media.querySelector('.b2c-video-poster');
      if (posterReset) {
        posterReset.style.height = '';
        posterReset.style.width = '';
      }
      changed = true;
    }

    if (wrapper) wrapper.classList.toggle('b2c-mobile-video-active', expanded);

    if (changed) {
      // Directe, animatie-onafhankelijke hoogte-update van de Flickity-
      // viewport (een hoogtewijziging raakt de horizontale cell-posities
      // niet). De nette resize hieronder her-meet daarna alles zodra
      // Flickity dat toelaat.
      if (flickity && flickity.isActive && flickity.viewport) {
        if (gallery.b2cBaseViewportHeight == null) {
          gallery.b2cBaseViewportHeight = flickity.viewport.style.height;
        }
        if (expanded) {
          var cellObj = typeof flickity.getCell === 'function'
            ? flickity.getCell(parts.cell)
            : null;
          if (cellObj && typeof cellObj.getSize === 'function') cellObj.getSize();
          var outerH = cellObj && cellObj.size
            ? cellObj.size.outerHeight
            : parts.cell.offsetHeight;
          if (outerH > 0) flickity.viewport.style.height = Math.round(outerH) + 'px';
        } else {
          flickity.viewport.style.height = gallery.b2cBaseViewportHeight;
        }
      }
      requestFlickityResize(flickity);
    }
  }

  function bindMobileVideoSelect(gallery) {
    var flickity = getFlickity(gallery.carousel);
    if (!flickity || gallery.carousel.b2cBoundFlickity === flickity) return;
    gallery.carousel.b2cBoundFlickity = flickity;
    flickity.on('select', function () {
      safe(function () {
        markNormalVideoSelect(gallery);
      });
      safe(function () {
        updateMobileVideoExpand(gallery);
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Modal / lightbox
   * ------------------------------------------------------------------ */

  function collectModals() {
    modalObservers.forEach(function (observer) {
      observer.disconnect();
    });
    modalObservers = [];
    modals = [];

    document.querySelectorAll('.product-media-modal .product-media-modal__content').forEach(function (content) {
      var modal = { content: content, items: emptyGroups() };
      registerModalItems(modal);
      modals.push(modal);

      // VariantSelects.updateMedia (global.js) vervangt de modal-inhoud via
      // innerHTML bij variantwissel; dan verschijnen verse video-nodes en
      // vervallen onze referenties. De observer herbouwt de registratie
      // ALLEEN wanneer er niet-geregistreerde nodes opduiken (= zo'n swap);
      // onze eigen remove/restore werkt met bekende nodes en triggert dus
      // geen rebuild, zodat referenties voor terugplaatsen behouden blijven
      // en er geen loop kan ontstaan.
      var observer = new MutationObserver(function () {
        safe(function () {
          var tracked = new Set();
          VISIBILITIES.forEach(function (key) {
            modal.items[key].forEach(function (item) {
              tracked.add(item.el);
            });
          });

          var hasUnknown = false;
          VISIBILITIES.forEach(function (key) {
            content
              .querySelectorAll('[data-media-visibility="' + key + '"]')
              .forEach(function (el) {
                if (!tracked.has(el)) hasUnknown = true;
              });
          });

          if (hasUnknown) {
            registerModalItems(modal);
            applyModal(modal);
          }
        });
      });
      observer.observe(content, { childList: true });
      modalObservers.push(observer);
    });
  }

  function registerModalItems(modal) {
    modal.items = emptyGroups();
    var children = Array.prototype.slice.call(modal.content.children);
    children.forEach(function (child, index) {
      var visibility = child.getAttribute && child.getAttribute('data-media-visibility');
      if (modal.items[visibility]) {
        modal.items[visibility].push({ el: child, index: index });
      }
    });
  }

  function applyModal(modal) {
    var isDesktop = desktopMql.matches;

    VISIBILITIES.forEach(function (visibility) {
      var shouldShow = isDesktop
        ? visibility === 'desktop-only'
        : visibility === 'mobile-only';

      if (shouldShow) {
        modal.items[visibility]
          .filter(function (item) { return !item.el.isConnected; })
          .sort(function (a, b) { return a.index - b.index; })
          .forEach(function (item) {
            var ref = modal.content.children[item.index] || null;
            modal.content.insertBefore(item.el, ref);
          });
      } else {
        modal.items[visibility].forEach(function (item) {
          if (!item.el.isConnected) return;
          pauseVideosIn(item.el);
          item.el.remove();
        });
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * Toepassen + init
   * ------------------------------------------------------------------ */

  function apply() {
    var isDesktop = desktopMql.matches;

    galleries.forEach(function (gallery) {
      safe(function () {
        if (isDesktop) {
          restoreSlides(gallery, 'desktop-only');
          removeSlides(gallery, 'mobile-only');
          restoreThumbs(gallery, 'desktop-only');
          removeThumbs(gallery, 'mobile-only');
        } else {
          removeSlides(gallery, 'desktop-only');
          restoreSlides(gallery, 'mobile-only');
          removeThumbs(gallery, 'desktop-only');
          restoreThumbs(gallery, 'mobile-only');
        }
        syncThumbnails(gallery);
        bindMobileVideoSelect(gallery);
        updateMobileVideoExpand(gallery);
      });
      safe(function () {
        setupNormalVideoGuards(gallery);
      });
    });

    modals.forEach(function (modal) {
      safe(function () {
        applyModal(modal);
      });
    });
  }

  function init() {
    safe(collectGalleries);
    safe(collectModals);
    apply();
  }

  // Voorwaarde 5: alleen matchMedia 'change', geen resize-polling.
  if (desktopMql.addEventListener) {
    desktopMql.addEventListener('change', apply);
  } else if (desktopMql.addListener) {
    desktopMql.addListener(apply);
  }

  // Theme editor: sectie wordt opnieuw gerenderd -> registraties opnieuw opbouwen.
  document.addEventListener('shopify:section:load', function () {
    safe(init);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
